import { useState, useRef, useEffect, useCallback, useMemo } from "react";

interface VideoClip {
  id: string;
  video_url: string;
  poster_url: string | null;
  start_time: number;
  end_time: number | null;
  source_type?: "upload" | "youtube" | "external";
}

interface ServiceVideoCarouselProps {
  videos: VideoClip[];
  fallbackImage?: string;
  isHovered?: boolean;
  className?: string;
  debug?: boolean;
}

// Extract YouTube video ID from various URL formats
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

// Determine source type from URL if not specified
function getSourceType(video: VideoClip): "upload" | "youtube" | "external" {
  if (video.source_type) return video.source_type;
  
  const url = video.video_url.toLowerCase();
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return "youtube";
  }
  if (url.includes("vimeo.com") || url.includes("cdn") || url.includes("cloudinary")) {
    return "external";
  }
  return "upload";
}

export function ServiceVideoCarousel({
  videos,
  fallbackImage,
  isHovered = false,
  className = "",
  debug = false,
}: ServiceVideoCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const preloadVideoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentVideo = videos[currentIndex];
  const nextVideo = videos[nextIndex % videos.length];
  const hasVideos = videos.length > 0;

  // Memoize source types
  const currentSourceType = useMemo(() => 
    currentVideo ? getSourceType(currentVideo) : "upload", 
    [currentVideo]
  );
  
  const isYouTube = currentSourceType === "youtube";

  // Intersection Observer for viewport detection
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3, rootMargin: "50px" }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Page visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current) {
        videoRef.current.pause();
      } else if (!document.hidden && isVisible && videoRef.current && !isYouTube) {
        videoRef.current.play().catch(() => {});
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isVisible, isYouTube]);

  // Preload next video
  useEffect(() => {
    if (!nextVideo || videos.length <= 1) return;
    
    const nextSourceType = getSourceType(nextVideo);
    if (nextSourceType === "youtube") return; // Can't preload YouTube
    
    const preloadVideo = preloadVideoRef.current;
    if (preloadVideo && nextVideo.video_url !== preloadVideo.src) {
      preloadVideo.src = nextVideo.video_url;
      preloadVideo.load();
    }
  }, [nextVideo, videos.length]);

  // Handle video playback and time-based trimming
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || !currentVideo) return;

    const endTime = currentVideo.end_time ?? video.duration;
    
    if (video.currentTime >= endTime - 0.1) {
      // Start transition to next video
      setIsTransitioning(true);
      
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % videos.length);
        setNextIndex((prev) => (prev + 1) % videos.length);
        setIsTransitioning(false);
      }, 300);
    }
  }, [currentVideo, videos.length]);

  // Reset video ready state when clip changes
  useEffect(() => {
    setVideoReady(false);
    setHasError(false);
  }, [currentIndex]);

  // Set up video when clip changes or becomes visible
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentVideo || isYouTube) return;

    const startTime = currentVideo.start_time || 0;
    
    const setupVideo = () => {
      video.currentTime = startTime;
      if (isVisible && !document.hidden) {
        video.play().catch(() => {
          // Silently handle autoplay failure
        });
      }
    };

    if (video.readyState >= 1) {
      setupVideo();
    } else {
      video.addEventListener("loadedmetadata", setupVideo, { once: true });
    }
  }, [currentIndex, currentVideo, isVisible, isYouTube]);

  // Play/pause based on visibility
  useEffect(() => {
    const video = videoRef.current;
    if (!video || isYouTube) return;

    if (isVisible && !document.hidden) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Silently ignore autoplay failures
        });
      }
    } else {
      video.pause();
    }
  }, [isVisible, isYouTube]);

  // Handle tap to restart on mobile
  const handleTap = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!("ontouchstart" in window)) return;
    
    const target = e.target as HTMLElement;
    if (target.closest("a") || target.closest("button")) return;
    
    e.preventDefault();
    setCurrentIndex(0);
    setNextIndex(1);
    
    const video = videoRef.current;
    if (video && currentVideo) {
      video.currentTime = currentVideo.start_time || 0;
      video.play().catch(() => {});
    }
  }, [currentVideo]);

  // Get YouTube embed URL with autoplay parameters
  const getYouTubeEmbedUrl = useCallback((video: VideoClip) => {
    const videoId = extractYouTubeId(video.video_url);
    if (!videoId) return null;
    
    const startTime = Math.floor(video.start_time || 0);
    const endTime = video.end_time ? Math.floor(video.end_time) : undefined;
    
    let url = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&modestbranding=1&playsinline=1&rel=0&enablejsapi=1`;
    
    if (startTime > 0) url += `&start=${startTime}`;
    if (endTime) url += `&end=${endTime}`;
    url += `&playlist=${videoId}`; // Required for loop
    
    return url;
  }, []);

  // Get YouTube thumbnail
  const getYouTubeThumbnail = useCallback((url: string) => {
    const videoId = extractYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  }, []);

  // Show fallback if no videos or all errored
  if (!hasVideos || hasError) {
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        {fallbackImage ? (
          <img
            src={fallbackImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-muted via-primary/10 to-muted" />
        )}
      </div>
    );
  }

  const posterImage = currentVideo?.poster_url 
    || (isYouTube ? getYouTubeThumbnail(currentVideo.video_url) : null)
    || fallbackImage;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${className}`}
      onClick={handleTap}
      onTouchEnd={handleTap}
    >
      {/* Background/poster layer - fades out when video is ready */}
      <div 
        className={`absolute inset-0 transition-opacity duration-500 ${
          videoReady ? "opacity-0" : "opacity-100"
        }`}
      >
        {posterImage ? (
          <img
            src={posterImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-muted via-primary/10 to-muted" />
        )}
      </div>

      {/* Hidden preload video */}
      <video
        ref={preloadVideoRef}
        className="hidden"
        muted
        playsInline
        preload="auto"
      />

      {/* Video layer */}
      <div className="absolute inset-0">
        {isYouTube ? (
          isVisible && (
            <iframe
              src={getYouTubeEmbedUrl(currentVideo) || undefined}
              className={`absolute inset-0 w-full h-full object-cover border-0 pointer-events-none transition-transform duration-700 ${
                isHovered ? "scale-110" : "scale-100"
              }`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              loading="lazy"
              onLoad={() => setVideoReady(true)}
            />
          )
        ) : (
          <video
            ref={videoRef}
            key={currentVideo?.video_url}
            src={currentVideo?.video_url}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
              isHovered ? "scale-110" : "scale-100"
            } ${videoReady ? "opacity-100" : "opacity-0"}`}
            muted
            playsInline
            autoPlay
            loop={videos.length === 1}
            preload="auto"
            onTimeUpdate={handleTimeUpdate}
            onCanPlay={() => setVideoReady(true)}
            onLoadedData={() => setVideoReady(true)}
            onPlaying={() => setVideoReady(true)}
            onError={() => setHasError(true)}
          />
        )}
      </div>

      {/* Video indicator dots for multiple clips */}
      {videos.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {videos.map((_, idx) => (
            <div
              key={idx}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? "bg-primary w-3"
                  : "bg-white/50"
              }`}
            />
          ))}
        </div>
      )}

      {/* Debug overlay - only visible when debug prop is true */}
      {debug && (
        <div className="absolute top-1 left-1 right-1 z-20 bg-black/80 text-[10px] text-white p-1.5 rounded font-mono leading-tight">
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            <span className={videoReady ? "text-green-400" : "text-red-400"}>
              ready: {videoReady ? "✓" : "✗"}
            </span>
            <span className={isVisible ? "text-green-400" : "text-yellow-400"}>
              visible: {isVisible ? "✓" : "✗"}
            </span>
            <span className={hasError ? "text-red-400" : "text-green-400"}>
              error: {hasError ? "✗" : "✓"}
            </span>
            <span>clip: {currentIndex + 1}/{videos.length}</span>
            <span className="text-blue-300">{currentSourceType}</span>
          </div>
          <div className="truncate text-muted-foreground/70 mt-0.5">
            {currentVideo?.video_url?.split('/').pop()?.slice(0, 30)}
          </div>
        </div>
      )}
    </div>
  );
}
